import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getTruckById } from '../services/truckService';
import TruckDocumentList from '../components/TruckDocumentList';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`truck-tabpanel-${index}`}
      aria-labelledby={`truck-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TruckDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [truck, setTruck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const truckResponse = await getTruckById(id);
        setTruck(truckResponse.data);
      } catch (err) {
        console.error('Error fetching truck details:', err);
        setError('Failed to load truck details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!truck) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">{error || 'Truck not found'}</Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/trucks')}
          sx={{ mt: 2 }}
        >
          Back to Trucks
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/trucks')}
          sx={{ mb: 2 }}
        >
          Back to Trucks
        </Button>

        <Typography variant="h4" gutterBottom>
          Truck #{truck.unitNumber}
        </Typography>

        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          {truck.make} {truck.model} ({truck.year})
        </Typography>

        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Truck Details" id="truck-tab-0" />
          <Tab label="Documents" id="truck-tab-1" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">VIN</Typography>
                  <Typography variant="body1" gutterBottom>{truck.vin}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Typography variant="body1" gutterBottom>{truck.status}</Typography>
                </Grid>
                <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Make</Typography>
                  <Typography variant="body1" gutterBottom>{truck.make}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Model</Typography>
                  <Typography variant="body1" gutterBottom>{truck.model}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Year</Typography>
                  <Typography variant="body1" gutterBottom>{truck.year}</Typography>
                </Grid>
                <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                  <Typography variant="body1">{truck.notes || 'No notes available'}</Typography>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={() => navigate('/trucks')}>
                  Manage in Truckbook
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TruckDocumentList truckId={id} truck={truck} />
        </TabPanel>
      </Box>
    </Container>
  );
};

export default TruckDetailPage;
